import type { FC, ReactNode } from 'react'
import type { FavoriteChange } from '@/lib/marketplace/saved-listings'

import { useEffect, useState } from 'react'
import { Heart } from 'lucide-react'

import { cn } from '@/lib/utils'
import { FAVORITE_EVENT, readSavedListings } from '@/lib/marketplace/saved-listings'

interface ListingLikeCountProps {
    listingId: string
    /** Aggregate count from the database, rendered on the server. */
    count: number
    className?: string
}

/**
 * The favourite count in a listing's metadata row. Display only: the heart the
 * visitor presses is the save control on the image, and duplicating it here
 * would nest a second control inside the row's link.
 *
 * The heart is filled brand red ONLY when this browser is one of the people
 * who favourited the listing, which is read after mount so the server and the
 * first client paint agree. When the visitor toggles the listing on this same
 * page the count moves with them straight away.
 */
export const ListingLikeCount: FC<ListingLikeCountProps> = ({ listingId, count, className }): ReactNode => {
    const [liked, setLiked] = useState(false)
    const [delta, setDelta] = useState(0)

    useEffect(() => {
        setLiked(readSavedListings().includes(listingId))
        setDelta(0)
    }, [listingId])

    useEffect(() => {
        const onChange = (event: Event): void => {
            const detail = (event as CustomEvent<FavoriteChange>).detail

            if (!detail || detail.listingId !== listingId) return

            setLiked(detail.saved)
            setDelta((current) => current + (detail.saved ? 1 : -1))
        }

        window.addEventListener(FAVORITE_EVENT, onChange)

        return () => window.removeEventListener(FAVORITE_EVENT, onChange)
    }, [listingId])

    const total = Math.max(0, count + delta)

    return (
        <span className={cn('inline-flex items-center gap-1.5', className)}>
            <Heart
                aria-hidden='true'
                className={cn('size-3.5', liked ? 'fill-current text-brand-red-ink' : 'text-ink-900/55')}
                strokeWidth={2}
            />
            <span className='tabular-nums'>{total}</span>
            <span className='sr-only'>{total === 1 ? 'person saved this listing' : 'people saved this listing'}</span>
        </span>
    )
}
